/**
 * Colourway swap on the product page.
 *
 * Every colour on this store is its own product, grouped by the
 * custom.swatch_group metafield, so the colour swatches rendered by
 * snippets/swatch-group.liquid are plain links to another product page.
 * Clicking one reloaded the whole page -- which is what made comparing two
 * colourways painful enough that shoppers opened new tabs instead (OB-5).
 *
 * A click now fetches the target product and swaps the contents of <main>,
 * the same move the theme's own combined-listing path makes
 * (_handleSwapProduct in assets/variant-select.js), then pushes the new URL.
 * The href is left on the anchor, so a modified click, "open in new tab", a
 * crawler, or a shopper with no JS still reaches the real product page.
 *
 * Two things the swap has to do beyond replacing the markup:
 *
 * 1. Every section inside <main> gets a `shopify:section:load` on the next
 *    frame -- the frame is Alpine's, which binds the new picker in a microtask
 *    and has to have finished before the fit finder reaches for its radios.
 *    That event is how the theme's own scripts learn their markup was
 *    replaced: it re-binds the fit finder and re-applies a stored size to the
 *    colourway just picked, and re-reads the swatch labels.
 *
 * 2. The back button. Entries we push share this document, so leaving them to
 *    the browser would show the previous colourway's URL over this colourway's
 *    markup. `popstate` swaps to whatever the address bar now holds, and a
 *    fetch that fails hands the navigation back to the browser.
 */
if (!window.Eurus.loadedScript.has('colourway-swap.js')) {
  window.Eurus.loadedScript.add('colourway-swap.js');

  (function () {
    'use strict';

    var WRAP = '[data-swatch-swap]';
    var SWATCH = '.x-swatch';
    var PENDING = 'is-pending';

    // Colourways fetched this visit, keyed by the URL asked for. Comparing two
    // colours back and forth is the whole point of the ticket, so the second
    // look at one is free.
    var cache = {};
    var controller = null;
    var swapped = false;

    // Clicking through three colours faster than the network answers should
    // land on the third, not the first to come back. Every request carries the
    // token it was made under; anything that is no longer the latest drops its
    // result on the floor.
    var latest = 0;

    function isPlainClick(e) {
      return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
    }

    function load(url) {
      if (cache[url]) return Promise.resolve(cache[url]);

      if (controller) controller.abort();
      controller = new AbortController();

      return fetch(url, { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Colourway ' + url + ' returned ' + response.status);
          return response.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          cache[url] = doc;
          return doc;
        });
    }

    function announceSections(root) {
      root.querySelectorAll('.shopify-section').forEach(function (section) {
        section.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: section.id.replace('shopify-section-', '') }
          })
        );
      });
    }

    function apply(doc) {
      var source = doc.querySelector('main');
      var destination = document.querySelector('main');
      if (!source || !destination) return false;

      var title = doc.querySelector('head title');
      if (title) document.title = title.textContent;

      // Whoever was on the swatch row is about to have the element under their
      // focus deleted, which drops a keyboard user back to the top of the
      // document. The colour they picked is the row's active chip afterwards.
      var active = document.activeElement;
      var keepFocus = !!(active && active.closest && active.closest(SWATCH));

      destination.innerHTML = source.innerHTML;

      if (keepFocus) {
        var current = destination.querySelector(WRAP + ' ' + SWATCH + '.is-active');
        if (current) current.focus({ preventScroll: true });
      }

      window.requestAnimationFrame(function () {
        announceSections(destination);
      });

      return true;
    }

    function clearPending() {
      document.querySelectorAll(SWATCH + '.' + PENDING).forEach(function (swatch) {
        swatch.classList.remove(PENDING);
        swatch.removeAttribute('aria-busy');
      });
    }

    function swap(url, push) {
      var token = ++latest;

      return load(url)
        .then(function (doc) {
          if (token !== latest) return;
          if (!apply(doc)) throw new Error('No <main> to swap');
          swapped = true;
          if (push) window.history.pushState({ colourway: url }, '', url);
        })
        .catch(function (err) {
          if (err.name === 'AbortError' || token !== latest) return;
          console.error(err);
          window.location.href = url;
        })
        .finally(function () {
          if (token === latest) clearPending();
        });
    }

    document.addEventListener('click', function (e) {
      if (!isPlainClick(e)) return;

      var swatch = e.target.closest && e.target.closest(SWATCH);
      if (!swatch || !swatch.closest(WRAP)) return;

      var url = swatch.getAttribute('href');
      if (!url) return;

      // The colour already on screen: swallow the click rather than refetch it.
      e.preventDefault();
      if (swatch.classList.contains('is-active')) return;

      clearPending();
      swatch.classList.add(PENDING);
      swatch.setAttribute('aria-busy', 'true');
      swap(url, true);
    });

    window.addEventListener('popstate', function () {
      if (!swapped) return;
      var url = window.location.pathname + window.location.search;
      var token = ++latest;
      load(url)
        .then(function (doc) {
          if (token !== latest) return;
          if (!apply(doc)) window.location.reload();
        })
        .catch(function (err) {
          if (err.name === 'AbortError' || token !== latest) return;
          window.location.reload();
        })
        .finally(clearPending);
    });
  })();
}
